import React, { useState, useEffect, useMemo } from 'react';
import CustomerForm from './CustomerForm.jsx';
import * as customerService from '../../../services/customerService';
import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useAuthContext } from '../../../services/authContext';
import { useTableSort, SortableTh } from './useTableSort.jsx';
import './Admin.scss';

const CUSTOMERS_QUERY = gql`
  query Customers {
    customers {
      id
      name
      industry
      startDate
      endDate
      notes
      dataAccess { product activities { activity } regions { region } }
      outlooks { key label }
      publicationAccess
      _createdTs
      _updatedTs
      _updatedBy
      _ts
    }
  }
`;

const USERS_QUERY = gql`
  query UsersForCount {
    users {
      id
      customerIds
    }
  }
`;

export default function CustomerList() {
  const [customers, setCustomers] = useState([]);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [error, setError] = useState(null);
  const [filterText, setFilterText] = useState('');

  const { msalInstance, account } = useAuthContext();

  const { data, loading, error: queryError, refetch } = useQuery(CUSTOMERS_QUERY);
  const { data: usersData } = useQuery(USERS_QUERY);

  const userCountByCustomer = React.useMemo(() => {
    const counts = {};
    for (const user of (usersData?.users ?? [])) {
      for (const cid of (user.customerIds ?? [])) {
        counts[cid] = (counts[cid] ?? 0) + 1;
      }
    }
    return counts;
  }, [usersData]);

  useEffect(() => {
    if (data?.customers) setCustomers(data.customers);
  }, [data]);

  const accessors = useMemo(() => ({
    userCount: row => userCountByCustomer[row.id] ?? 0,
  }), [userCountByCustomer]);

  const { sortedRows, sortKey, sortDir, toggleSort } = useTableSort(customers, {
    defaultKey: 'name',
    accessors,
    filterFields: ['name', 'industry'],
    filterText,
  });

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
  };

  const handleAdd = () => setEditingCustomer({});

  const handleClose = () => setEditingCustomer(null);

  const handleSave = async (data) => {
    setError(null);
    try {
      if (editingCustomer?.id) {
        const updated = await customerService.updateCustomer(msalInstance, account, editingCustomer.id, data);
        setCustomers(prev => prev.map(c => c.id === updated.id ? updated : c));
        setEditingCustomer(prev => ({ ...prev, ...updated }));
        refetch();
        return updated;
      } else {
        const created = await customerService.createCustomer(msalInstance, account, data);
        setCustomers(prev => [...prev, created]);
        setEditingCustomer(prev => ({ ...prev, ...created }));
        refetch();
        return created;
      }
    } catch (e) {
      throw e;
    }
  };

  const handleDelete = async (customer) => {
    setError(null);
    try {
      await customerService.deleteCustomer(msalInstance, account, customer.id);
      setCustomers(prev => prev.filter(c => c.id !== customer.id));
      refetch();
      handleClose();
    } catch (e) {
      setError('Failed to delete customer');
    }
  };

  // ── Edit view (inline) ───────────────────────────────────────────────────────
  if (editingCustomer !== null) {
    return (
      <CustomerForm
        customer={editingCustomer}
        onSave={handleSave}
        onCancel={handleClose}
        onDelete={handleDelete}
      />
    );
  }

  // ── List view ────────────────────────────────────────────────────────────────
  return (
    <div>
      {error       && <div style={{ color: 'red', marginBottom: 8 }}>{error}</div>}
      {queryError  && <div style={{ color: 'red', marginBottom: 8 }}>{queryError.message}</div>}

      <div className="users-header" style={{ marginBottom: 16 }}>
        <h3>Customers</h3>
        <input
          type="text"
          placeholder="Search…"
          value={filterText}
          onChange={e => setFilterText(e.target.value)}
          className="admin-quick-filter"
        />
        <button className="btn btn-primary" onClick={handleAdd}>Add Customer</button>
      </div>

      {loading ? (
        <div>Loading customers...</div>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <SortableTh label="Name"     sortKeyName="name"      sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
              <SortableTh label="Industry" sortKeyName="industry"  sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
              <SortableTh label="Users"    sortKeyName="userCount" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.map(customer => (
              <tr key={customer.id}>
                <td>{customer.name}</td>
                <td>{customer.industry}</td>
                <td>{userCountByCustomer[customer.id] ?? 0}</td>
                <td>
                  <button className="btn-edit" onClick={() => handleEdit(customer)}>Edit</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
